import React from 'react';
import { Card, CardBody } from '../ui/Card';
import PageHeader from '../ui/PageHeader';
import { SectionCardHeader } from '../ui/SectionCard';
import { LifebuoyIcon, BookOpenIcon, SignalIcon } from '@heroicons/react/24/outline';

/**
 * SupportPage — Settings › Help & Support.
 *
 * Three sections, each a distinct concept:
 *   • Contact Support — email + phone (LifebuoyIcon: canonical "support" glyph)
 *   • Help Center     — link out to /help (BookOpenIcon: mirrors Header's Help
 *                        Center nav glyph — same destination, same glyph)
 *   • System Status   — connectivity/health indicator (SignalIcon)
 *
 * 2026-07-31 (§3.1 SectionHeader wave): each section switched from bare
 * <CardHeader>{title}</CardHeader> to <SectionCardHeader> with icon +
 * description to match the Customer Types reference. See SectionCard.js
 * docstring for the rev-5 supersession note.
 */
export default function SupportPage() {
  return (
    <div>
      <PageHeader
        title="Help & Support"
        description="Get help with your account or reach out to our support team."
      />

      <div className="space-y-4">
        {/* Contact Support */}
        <Card>
          <SectionCardHeader
            title="Contact Support"
            description="Reach the BIDIT support team by email or phone during business hours."
            icon={LifebuoyIcon}
          />
          <CardBody>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-500 text-sm font-medium w-16">Email:</span>
                <a
                  href="mailto:support@bidit.com"
                  className="text-red-600 hover:text-red-800 hover:underline"
                >
                  support@bidit.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-500 text-sm font-medium w-16">Phone:</span>
                <a
                  href="tel:+15551234567"
                  className="text-red-600 hover:text-red-800 hover:underline"
                >
                  (555) 123-4567
                </a>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Help Center — same glyph as Header's Help Center nav (BookOpenIcon → /help). Mirror pattern. */}
        <Card>
          <SectionCardHeader
            title="Help Center"
            description="Browse articles, guides, and FAQs to find answers quickly."
            icon={BookOpenIcon}
          />
          <CardBody>
            <a
              href="/help"
              className="inline-flex items-center text-red-600 hover:text-red-800 hover:underline font-medium text-sm"
            >
              Visit Help Center &rarr;
            </a>
          </CardBody>
        </Card>

        {/* System Status */}
        <Card>
          <SectionCardHeader
            title="System Status"
            description="Current operational status of BIDIT services."
            icon={SignalIcon}
          />
          <CardBody>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm text-gray-700">All systems operational</span>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
